/**
 * ErrorScreen Component
 *
 * Displays friendly error messages for magic link validation failures.
 * Different error codes show different icons and messages.
 */

interface ErrorScreenProps {
  message: string;
  code?: 'EXPIRED' | 'INVALID' | 'EVENT_NOT_FOUND' | 'PARTICIPANT_NOT_FOUND';
  secondaryMessage?: string;
  onRetry?: () => void;
  testId?: string;
}

export function ErrorScreen({
  message,
  code,
  secondaryMessage,
  onRetry,
  testId = 'error-screen',
}: ErrorScreenProps) {
  // Map error codes to icons
  const getIcon = () => {
    switch (code) {
      case 'EXPIRED':
        return '⏰';
      case 'EVENT_NOT_FOUND':
        return '🚫';
      case 'INVALID':
      case 'PARTICIPANT_NOT_FOUND':
      default:
        return '❌';
    }
  };

  // Get secondary message if not provided
  const getSecondaryMessage = () => {
    if (secondaryMessage) return secondaryMessage;
    if (code === 'EXPIRED') return 'Links are valid for 7 days';
    return null;
  };

  const icon = getIcon();
  const secondary = getSecondaryMessage();

  return (
    <div
      className="min-h-screen bg-gray-50 flex items-center justify-center px-4"
      data-testid={testId}
    >
      <div className="max-w-md w-full text-center">
        {/* Error Icon */}
        <div className="text-6xl mb-6">{icon}</div>

        {/* Error Message */}
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          {message}
        </h1>

        {/* Secondary Message */}
        {secondary && (
          <p className="text-sm text-gray-600 mb-8">
            {secondary}
          </p>
        )}

        {/* Help Text */}
        {code === 'EXPIRED' || code === 'INVALID' ? (
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <p className="text-gray-700">
              Ask your friend to send a new invite
            </p>
          </div>
        ) : null}

        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="mt-6 w-full rounded-lg bg-primary px-5 py-3 font-bold text-white shadow-sm transition-colors hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Try again
          </button>
        ) : null}
      </div>
    </div>
  );
}
